import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Modal, Collapse } from 'react-bootstrap';
import { IoEye } from "react-icons/io5";
import { MdContentCopy, MdDeleteOutline, MdKeyboardArrowDown, MdFileDownload } from "react-icons/md";
import SkeletonLoader from "../common/SkeletonLoader";
import SubscriptionFiltersBar from "./SubscriptionFiltersBar";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import { FiTag, FiTarget, FiRefreshCw } from "react-icons/fi";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
    Utils,
    getSubscriptionDisplayStatus,
    fetchWithAuth,
    copyToClipboard,
    EMPTY_SUBSCRIPTION_LIST_FILTERS,
    buildSubscriptionListBody,
    filtersObjectHasAnyValue,
    getDefaultPageSize,
    getTotalPages,
    can,
    formatDateTime,
    getBrowserTimeZone,
    exportListCsv,
} from "../../utils";
import ListPaginationBar from "../common/ListPaginationBar";

const EMPTY_SUB_FILTERS = EMPTY_SUBSCRIPTION_LIST_FILTERS;

function parseSubscriptionFiltersFromSearchParams(searchParams) {
    if (!searchParams) return { ...EMPTY_SUB_FILTERS };
    let device_type = searchParams.get('device_type') || '';
    if (!device_type) {
        const legacy = searchParams.get('device_os');
        if (legacy) device_type = legacy;
    }
    return {
        subscription_id: searchParams.get('subscription_id') || '',
        notify_sphere_id: searchParams.get('notify_sphere_id') || '',
        email: searchParams.get('email') || '',
        phone: searchParams.get('phone') || '',
        device_model: searchParams.get('device_model') || '',
        device_type,
        status: searchParams.get('status') || '',
        name: searchParams.get('name') || '',
        dateFrom: searchParams.get('date_from') || '',
        dateTo: searchParams.get('date_to') || '',
        datePreset: searchParams.get('preset') || '',
    };
}

function syncSubscriptionFiltersToUrl(filters, navigate, appId) {
    const params = new URLSearchParams();
    const add = (k, v) => {
        const t = (v || '').trim();
        if (t) params.set(k, t);
    };
    add('subscription_id', filters.subscription_id);
    add('notify_sphere_id', filters.notify_sphere_id);
    add('email', filters.email);
    add('phone', filters.phone);
    add('device_model', filters.device_model);
    add('device_type', filters.device_type);
    add('status', filters.status);
    add('name', filters.name);
    add('date_from', filters.dateFrom);
    add('date_to', filters.dateTo);
    if ((filters.datePreset || '').trim() && filters.datePreset !== 'custom') {
        params.set('preset', filters.datePreset.trim());
    }
    const qs = params.toString();
    navigate(`/apps/${appId}/subscriptions${qs ? `?${qs}` : ''}`, { replace: true });
}

const Subscriptions = () => {

    const { appId } = useParams()
    const navigate = useNavigate();
    const [appUsers, setAppUsers] = useState([])
    const tableScrollRef = useRef(null);
    const tableInnerRef = useRef(null);

    const {
        openActionId: isOpen,
        openActionItem: openItem,
        dropdownPosition,
        dropdownRef,
        closeDropdown,
        handleDropdownAction,
        handleOpenDropdown,
    } = useActionDropdown({
      tableScrollRef,
      optionCount: () =>
        3 + (can("subscriptions.edit") ? 1 : 0) + (can("subscriptions.delete") ? 1 : 0),
      menuWidth: 240,
    });

    const tableMaxHeight = useTableMaxHeight(tableScrollRef);
    const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, appUsers?.length);

    const [showModal, setShowModal] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [noDataFound, setNoDataFound] = useState(false) // eslint-disable-line no-unused-vars
    const [isLoading, setIsLoading] = useState(true)
    const [isExporting, setIsExporting] = useState(false);
    const [searchParams] = useSearchParams();

    const [draftFilters, setDraftFilters] = useState(() =>
        parseSubscriptionFiltersFromSearchParams(
            typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
        )
    );
    const [appliedFilters, setAppliedFilters] = useState(() =>
        parseSubscriptionFiltersFromSearchParams(
            typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
        )
    );
    const [deviceTypeOptions, setDeviceTypeOptions] = useState([]);

    const [segmentId, setSegmentId] = useState(() => {
        if (typeof window === 'undefined') return null;
        return new URLSearchParams(window.location.search).get('segment_id');
    });
    const [segmentName, setSegmentName] = useState('');
    const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);

    useEffect(() => {
        const hasContent =
            filtersObjectHasAnyValue(draftFilters) || filtersObjectHasAnyValue(appliedFilters);
        if (!hasContent) {
            setFilterAccordionOpen(false);
            return;
        }
        setFilterAccordionOpen(true);
    }, [draftFilters, appliedFilters]);

    const pageSize = getDefaultPageSize();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const totalPages = getTotalPages(totalRows, pageSize);


    const handleOpenModal = (id) => {
        closeDropdown();
        setSelectedRowId(id);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setInputValue('');
    };

    const handleSubmit = () => {
        // update/app/users

        if (inputValue.length) {

            fetchWithAuth(Utils.baseurl + 'update/app/users', {
                method: 'POST',
                body: JSON.stringify({ id: selectedRowId, name: inputValue, isTest: 1 }),
            })
                .then((response) => response.json())
                .then(async (result) => {
                    if (result.status) {
                        // handleCallRenderList()
                        handleCloseModal();
                    }
                })
                .catch((error) => console.error(error));
        }

        handleCloseModal();
    };


    function getAppUser(filters) {
        let bodyObj = { id: appId }
        let endpoint = 'get/app/subscriptions';

        // If segment_id is provided, use the segment subscriptions endpoint
        if (filters?.segment_id) {
            endpoint = 'get/segment/subscriptions';
            bodyObj = {
                app_id: appId,
                segment_id: filters.segment_id
            };
        } else {
            const f = filters || {};
            Object.assign(bodyObj, buildSubscriptionListBody(f));
            if (f.device_os) bodyObj.device_os = f.device_os;
        }

        setIsLoading(true);
        fetchWithAuth(Utils.baseurl + endpoint, {
            method: 'POST',
            body: JSON.stringify({
                ...bodyObj,
                page: filters?.page || 1,
                pageSize,
                timezone: getBrowserTimeZone() || undefined,
            }),
        })
            .then((response) => response.json())
            .then(async (result) => {
                setIsLoading(false);
                if (result.status) {

                    // Handle different response formats
                    let subscriptionData = result.data.appSubscriptionData || result.data;
                    if (result.data?.meta?.total !== null && result.data?.meta?.total !== undefined) {
                        setTotalRows(parseInt(result.data.meta.total, 10) || 0);
                    } else if (Array.isArray(subscriptionData)) {
                        setTotalRows(subscriptionData.length);
                    } else {
                        setTotalRows(0);
                    }

                    // If using segment endpoint, the data structure might be different
                    if (Array.isArray(subscriptionData)) {
                        setAppUsers(subscriptionData)
                        if (!subscriptionData.length) {
                            setNoDataFound(true)
                        } else {
                            setNoDataFound(false)
                        }
                    } else {
                        setAppUsers([])
                        setNoDataFound(true)
                    }
                }

            })
            .catch((error) => {
                console.error(error);
                setIsLoading(false);
            });
    }

    useEffect(() => {
        if (!segmentId) {
            setSegmentName('');
            return;
        }
        const n = searchParams.get('segment_name');
        setSegmentName(n || 'Segment');
    }, [segmentId, searchParams]);

    useEffect(() => {
        if (segmentId) {
            getAppUser({ segment_id: segmentId, page: currentPage });
            return;
        }
        getAppUser({ ...appliedFilters, page: currentPage });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, appliedFilters, segmentId]);

    useEffect(() => {
        if (segmentId) return;
        fetchWithAuth(Utils.baseurl + 'get/app/sub-device-os', {
            method: 'POST',
            body: JSON.stringify({ id: appId }),
        })
            .then((response) => response.json())
            .then((result) => {
                if (result.status && Array.isArray(result.data?.deviceTypes)) {
                    setDeviceTypeOptions(result.data.deviceTypes);
                }
            })
            .catch(() => { });
    }, [appId, segmentId]);

    const handleApplyFilters = () => {
        setAppliedFilters({ ...draftFilters });
        setCurrentPage(1);
        syncSubscriptionFiltersToUrl(draftFilters, navigate, appId);
    };

    const handleResetFilters = () => {
        const cleared = { ...EMPTY_SUB_FILTERS };
        setDraftFilters(cleared);
        setAppliedFilters(cleared);
        setCurrentPage(1);
        navigate(`/apps/${appId}/subscriptions`, { replace: true });
    };




    async function handleAction(type, id) {

        if (type === "Delete") {
            closeDropdown();

            const confirm = await Swal.fire({
                title: "Are you sure?",
                text: "This subscription will be permanently deleted.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, delete",
            });
            if (!confirm.isConfirmed) return;

            fetchWithAuth(Utils.baseurl + 'delete/app/subscriptions', {
                method: 'POST',
                body: JSON.stringify({ id: id }),
            })
                .then((response) => response.json())
                .then(async (result) => {
                    if (result.status) {
                        getAppUser(
                            segmentId
                                ? { segment_id: segmentId, page: currentPage }
                                : { ...appliedFilters, page: currentPage }
                        );
                    }
                })
                .catch((error) => console.error(error));
        }

        if (type === "addtest") {
            closeDropdown();
            // navigate(`/apps/${id}`)
        }

    }


    let loaderData = [
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        },
        {
            id: 1,
            name: 'test',
        }
    ]

    const handleExport = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const isSegment = Boolean(segmentId);
            const { status, message } = await exportListCsv(
                isSegment ? "get/segment/subscriptions" : "get/app/subscriptions",
                isSegment
                    ? { app_id: appId, segment_id: segmentId, timezone: getBrowserTimeZone() || undefined }
                    : {
                        id: appId,
                        ...buildSubscriptionListBody(appliedFilters),
                        ...(appliedFilters.device_os ? { device_os: appliedFilters.device_os } : {}),
                        timezone: getBrowserTimeZone() || undefined,
                    },
                "subscription-records.csv"
            );
            if (!status) {
                toast.error(message);
                return;
            }
            if (message) toast.info(message);
        } catch (error) {
            console.error(error);
            toast.error("Export failed");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="dashboard_right_top">
                    <div className="dashboard_heading">
                        <h1>Subscription Records</h1>
                    </div>

                    <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        <button
                            type="button"
                            className="add_app_btn_secondary btn_theme_secondary"
                            title="Refresh list"
                            onClick={() => {
                                setIsLoading(true);
                                getAppUser(segmentId ? { segment_id: segmentId, page: currentPage } : { ...appliedFilters, page: currentPage });
                            }}
                        >
                            <FiRefreshCw />
                            <span>Refresh</span>
                        </button>
                        <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
                            <MdFileDownload />
                            <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                        </button>
                    </div>


                </div>

                {!segmentId && (
                    <div className="list-page-filter-card">
                        <button
                            type="button"
                            className="list-page-filter-accordion-header"
                            onClick={() => setFilterAccordionOpen((o) => !o)}
                            aria-expanded={filterAccordionOpen}
                            aria-controls="subscription-records-filter-panel"
                            id="subscription-records-filter-accordion"
                        >
                            <span className="list-page-filter-accordion-title">Filter</span>
                            <MdKeyboardArrowDown
                                className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? 'list-page-filter-accordion-chevron--open' : ''}`}
                                size={22}
                                aria-hidden
                            />
                        </button>
                        <Collapse in={filterAccordionOpen}>
                            <div className="list-page-filter-accordion-body" id="subscription-records-filter-panel" role="region" aria-labelledby="subscription-records-filter-accordion">
                                <SubscriptionFiltersBar
                                    draftFilters={draftFilters}
                                    setDraftFilters={setDraftFilters}
                                    onSearch={handleApplyFilters}
                                    onReset={handleResetFilters}
                                    deviceTypeOptions={deviceTypeOptions}
                                    includeNameField={false}
                                    instanceIdPrefix="subs-records"
                                />
                            </div>
                        </Collapse>
                    </div>
                )}

                <div className="dashboard_app_table dashboard_app_table--list-records">
                    {segmentId && (
                        <div style={{
                            padding: "12px 16px",
                            backgroundColor: "#e7f3ff",
                            border: "1px solid #b3d9ff",
                            borderRadius: "6px",
                            marginBottom: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ fontSize: "14px", color: "#0a1061", fontWeight: "500" }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <FiTag size={14} />
                                        <span>Filtered by Segment: {segmentName}</span>
                                    </span>
                                </span>
                            </div>
                            <button
                                onClick={() => {
                                    const cleared = { ...EMPTY_SUB_FILTERS };
                                    setDraftFilters(cleared);
                                    setAppliedFilters(cleared);
                                    setSegmentId(null);
                                    setSegmentName('');
                                    setCurrentPage(1);
                                    navigate(`/apps/${appId}/subscriptions`, { replace: true });
                                }}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    color: "#0a1061",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    textDecoration: "underline"
                                }}
                            >
                                Clear Filter
                            </button>
                        </div>
                    )}
                    <div className="dashboard_table">
                        <div
                            ref={tableScrollRef}
                            className="table_scroll_wrapper"
                        >
                            <div
                                ref={tableInnerRef}
                                className={`table_inner table_y_scroll ${isAtScrollEnd ? 'at-scroll-end' : ''}`}
                                style={{
                                    maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : 'calc(100vh - 320px)',
                                    overflowY: 'auto',
                                    overflowX: 'auto'
                                }}
                            >
                                <table className="table_outer table-sm">
                                    <thead>
                                        <tr>
                                            <th className="col-status" style={{ textAlign: "center" }}>Status</th>
                                            <th className="col-subscription-id" style={{ textAlign: "center" }}>Subscription ID</th>
                                            <th className="col-notify-sphere-id" style={{ textAlign: "center" }}>Notify Sphere ID</th>
                                            <th className="col-email">Email</th>
                                            <th className="col-phone">Phone</th>
                                            <th className="col-device-name">Device Name</th>
                                            <th className="col-android-version">Device</th>
                                            <th className="col-token" >Push Token</th>
                                            <th style={{ textAlign: "center" }}>Created</th>
                                            <th className="col-action" style={{ textAlign: "center" }}></th>
                                            {/* <th className="col-latitude" style={{ textAlign: "center" }}>Latitude</th>
                                            <th className="col-longitude" style={{ textAlign: "center" }}>Longitude</th>   */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appUsers.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" style={{ textAlign: "center" }}>
                                                    {
                                                        isLoading ?
                                                            <SkeletonLoader columns={loaderData} rows={2} />
                                                            // <FontAwesomeIcon icon={faSpinner} style={{ marginTop: "10px" }} spin size="2x" />
                                                            : <p  >No data found</p>
                                                    }
                                                </td>
                                            </tr>
                                        ) : appUsers.map((item, index) => {
                                            const statusUi = getSubscriptionDisplayStatus(item.status);
                                            return (
                                                <tr key={item.id}>
                                                    <td className="col-status" style={{ textAlign: "center" }}>
                                                        {statusUi.badgeClass ? (
                                                            <span className={statusUi.badgeClass}>{statusUi.label}</span>
                                                        ) : (
                                                            <span>{statusUi.label}</span>
                                                        )}
                                                    </td>
                                                    <td className="col-subscription-id" style={{ textAlign: "center" }}><span title={item.subscription_id}>{item.subscription_id}</span> </td>
                                                    <td className="col-notify-sphere-id" style={{ textAlign: "center" }}><span title={item.app_user?.notify_sphere_id}>{item.app_user?.notify_sphere_id ?? '—'}</span> </td>
                                                    <td className="col-email"><span title={item.app_user?.email}>{item.app_user?.email ?? '—'}</span></td>
                                                    <td className="col-phone"><span title={item.app_user?.phone}>{item.app_user?.phone ?? '—'}</span></td>
                                                    <td className="col-device-name"><span title={item.device_model}>{item.device_model}</span></td>
                                                    <td className="col-android-version" style={{ textAlign: "center" }}><span title={[item.device_os, item.device_version].filter(Boolean).join(' / ')}>{item.device_os}</span> </td>
                                                    <td className="col-token" style={{ textAlign: "left" }}><span title={item.token}>{item.token}</span></td>
                                                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                        {formatDateTime(item.createdAt) || '—'}
                                                    </td>
                                                    <td className="col-action" style={{ textAlign: "center" }}>
                                                        <button
                                                            type="button"
                                                            className="dropdown-trigger-btn btn btn-link p-0"
                                                            aria-haspopup="true"
                                                            aria-expanded={isOpen === item.id}
                                                            onClick={(e) => handleOpenDropdown(e, item.id, item)}
                                                        >
                                                            <svg viewBox="0 0 16 16" fill="currentColor">
                                                                <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" fill="currentColor">
                                                                </path>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                    {/* <td className="col-latitude" style={{ textAlign: "center" }}><span>{item.lat}</span></td>
                                                    <td className="col-longitude" style={{ textAlign: "center" }}><span>{item.long}</span></td> */}


                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <ListPaginationBar
                                page={currentPage}
                                setPage={setCurrentPage}
                                total={totalRows}
                                pageSize={pageSize}
                                totalPages={totalPages}
                                ariaLabel="Subscriptions pagination"
                                loading={isLoading}
                            />
                            {/* Modal */}
                            <Modal show={showModal} onHide={handleCloseModal} className="ns-modal">
                                <Modal.Header closeButton>
                                    <Modal.Title>Add Test User</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <span style={{ fontSize: "15px" }}> Enter a name for this test user :</span>

                                    <input
                                        style={{ marginTop: "6px" }}
                                        type="text"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        placeholder=""
                                        className="form-control"
                                    />
                                </Modal.Body>
                                <Modal.Footer>
                                    <button type="button" className="btn_theme_secondary" onClick={handleCloseModal}>
                                        Cancel
                                    </button>
                                    <button type="button" className="add_app_btn btn_theme_primary" onClick={handleSubmit}>
                                        <span>Add</span>
                                    </button>
                                </Modal.Footer>
                            </Modal>
                        </div>
                    </div>
                </div>
            </div>
            {openItem && dropdownPosition && createPortal(
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'fixed',
                        left: dropdownPosition.left,
                        top: dropdownPosition.top,
                        zIndex: 999999,
                        minWidth: '220px',
                    }}
                >
                    <div className="dropdown-menu-subscription">
                        <button
                            onClick={() => handleDropdownAction(() => navigate(`/apps/${appId}/subscriptions/${openItem.id}`))}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                            <IoEye size={20} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>View Subscription Profile</span>
                        </button>
                        <button
                            onClick={() => handleDropdownAction(() => copyToClipboard(openItem.subscription_id))}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                            <MdContentCopy size={20} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Copy Subscription ID</span>
                        </button>
                        <button
                            onClick={() => handleDropdownAction(() => copyToClipboard(openItem.token))}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                            <MdContentCopy size={20} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Copy Push Token</span>
                        </button>
                        {can("subscriptions.edit") ? (
                        <button
                            onClick={() => handleOpenModal(openItem.id)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                            <FiTarget size={20} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Add as Test Subscription</span>
                        </button>
                        ) : null}
                        {can("subscriptions.delete") ? (
                        <button
                            className="delete-class"
                            onClick={() => handleDropdownAction(() => handleAction('Delete', openItem.id))}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                            <MdDeleteOutline size={20} style={{ marginRight: '8px', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.875rem' }}>Delete Subscription</span>
                        </button>
                        ) : null}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Subscriptions;
